import type { LocaleString } from '@ark/web-shared/use';

export interface PredictRenderConfig {
  activityId: string;
}

/** 玩法类型（与后端领域模型 Prediction.kind 对齐） */
export type SessionKind =
  | 'EVENT_PREDICTION'
  | 'STOCK_RISE_FALL'
  | 'STOCK_CLOSING_PRICE';

export const SESSION_KIND = {
  EVENT_PREDICTION: 'EVENT_PREDICTION',
  STOCK_RISE_FALL: 'STOCK_RISE_FALL',
  STOCK_CLOSING_PRICE: 'STOCK_CLOSING_PRICE',
} as const satisfies Record<SessionKind, SessionKind>;

export interface PredictOption {
  code: string;
  label: LocaleString;
  /** 得票占比（0-100）；未开始时可缺省 */
  percent?: number;
}

export type PredictStatus =
  | 'upcoming'
  | 'ongoing'
  | 'closed'
  | 'settling'
  | 'settled';

export const PREDICT_STATUS = {
  UPCOMING: 'upcoming',
  ONGOING: 'ongoing',
  CLOSED: 'closed',
  SETTLING: 'settling',
  SETTLED: 'settled',
} as const satisfies Record<string, PredictStatus>;

/** 已结束时的个人结果 */
export type PredictResult = 'win' | 'lose' | 'push' | 'not_joined';

export const PREDICT_RESULT = {
  WIN: 'win',
  LOSE: 'lose',
  PUSH: 'push',
  NOT_JOINED: 'not_joined',
} as const satisfies Record<string, PredictResult>;

/** 股票行情展示数据（STOCK_RISE_FALL 玩法专用） */
export interface PredictStockInfo {
  /** 价格展示文本，如 "$431.310" */
  price: string;
  /** 涨跌额展示文本，如 "+32.31" */
  change: string;
  /** 涨跌幅展示文本，如 "+12.21%" */
  changePercent: string;
  /** 是否上涨；决定颜色（涨绿跌红） */
  isUp: boolean;
  /** 走势图采样点；按值原样绘制即可 */
  trendPoints?: number[];
}

/** 理由预设选项 */
export interface PresetReason {
  code: string;
  /** 多语言标签（后端下发 Record<string, string>） */
  label: Record<string, string>;
}

/** 理由配置（后端 market.reasonConfig 映射） */
export interface ReasonConfig {
  required: boolean;
  allowCustom: boolean;
  presetReasons?: PresetReason[];
}

/** 答案规格（后端 answerSpec 映射） */
export interface AnswerSpec {
  kind: 'CATEGORICAL' | 'SCALAR';
  options?: { code: string; label: LocaleString; percent?: number }[];
  scalar?: {
    precision?: number;
    min?: number;
    max?: number;
    referencePrice?: string | number;
  };
}

/** 单个竞猜场次（渲染数据单元） */
export interface PredictSession {
  id: string;
  /** 玩法类型，决定渲染哪个子组件 */
  kind: SessionKind;
  status: PredictStatus;
  question: { title: LocaleString; desc?: LocaleString };
  /** 已参与人数 */
  participantCount: number | string;
  /** 答案规格（含选项列表） */
  answerSpec: AnswerSpec;
  /** 我的投票项 code；未参与为 null */
  myVoteCode: string | null;
  /** 结算结果状态（后端判定） */
  settleStatus: 'PENDING' | 'WON' | 'LOST' | 'PUSH' | null;
  /** 最大可修改次数，-1=不限制, 0=提交即锁 */
  maxModifyCount: number;
  /** 当前已修改次数 */
  modifyCount: number;
  /** 数据源信息 */
  dataSource?: {
    type: 'STOCK_API' | 'MANUAL';
    stockId?: string;
    targetDate?: string;
  } | null;
  /** 正确值；未揭晓为 null */
  correctValue: string | null;
  /** 截止时间戳（毫秒） */
  deadline: number;
  /** 结算/公布结果时间戳（毫秒） */
  resolveAt: number;
  /** 股票行情数据（仅 STOCK_RISE_FALL 玩法用） */
  stock?: PredictStockInfo;
  /** 理由配置（需要填理由时从后端下发） */
  reasonConfig?: ReasonConfig;
}

/** 渲染布局：平铺 / 轮播 */
export type SessionLayout = 'tile' | 'carousel';
